import React, { useCallback, useEffect, useState } from 'react';
import { classes } from './ExpressCheckoutButtons.st.css';
import { useExpressCheckoutButtonsData } from '../provider/useExpressCheckoutButtonsData';
import loadable from '@loadable/component';
import { useFunctionResultObservation } from '@wix/function-result-observation';
import { RESULT_PROP_NAME } from '../utils/consts';
import { getEstimatedPaymentData } from '../utils/getEstimatedPaymentData';
import { Skeleton } from './Skeleton';
import { ExpressCheckoutButtonsState, getState } from '../utils/ExpressCheckoutButtonsState';
import { ExpressCheckoutErrorModal } from './ExpressCheckoutErrorModal/ExpressCheckoutErrorModal';
import { useCheckoutErrorModal } from './ExpressCheckoutErrorModal/useCheckoutErrorModal';

// @ts-expect-error - @types/loadable__component has nested React types incompatible with React 18
const CashierExpressCheckoutWidgetLazy = loadable({
  resolved: {},
  chunkName() {
    return "CashierExpressCheckoutWidget";
  },
  isReady(props) {
    const key = this.resolve(props);
    if (this.resolved[key] !== true) {
      return false;
    }
    if (typeof __webpack_modules__ !== 'undefined') {
      return !!__webpack_modules__[key];
    }
    return false;
  },
  importAsync: () => import(/* webpackChunkName: "CashierExpressCheckoutWidget" */'@wix/cashier-express-checkout-widget'),
  requireAsync(props) {
    const key = this.resolve(props);
    this.resolved[key] = false;
    return this.importAsync(props).then(resolved => {
      this.resolved[key] = true;
      return resolved;
    });
  },
  requireSync(props) {
    const id = this.resolve(props);
    if (typeof __webpack_require__ !== 'undefined') {
      return __webpack_require__(id);
    }
    return eval('module.require')(id);
  },
  resolve() {
    if (require.resolveWeak) {
      return require.resolveWeak("@wix/cashier-express-checkout-widget");
    }
    return eval('require.resolve')("@wix/cashier-express-checkout-widget");
  }
}, {
  resolveComponent: components => components.CashierExpressCheckoutWidget
});
export let ExpressCheckoutButtonsDataHooks = /*#__PURE__*/function (ExpressCheckoutButtonsDataHooks) {
  ExpressCheckoutButtonsDataHooks["root"] = "ExpressCheckoutButtonsDataHooks.root";
  ExpressCheckoutButtonsDataHooks["loader"] = "ExpressCheckoutButtonsDataHooks.loader";
  return ExpressCheckoutButtonsDataHooks;
}({});

/* eslint-disable sonarjs/cognitive-complexity */
export const ExpressCheckoutButtons = _ref => {
  let layout = _ref.layout,
    theme = _ref.theme,
    buttonStyle = _ref.buttonStyle,
    isShippable = _ref.isShippable,
    estimatedTotal = _ref.estimatedTotal,
    _ref$canStartPayment = _ref.canStartPayment,
    canStartPayment = _ref$canStartPayment === void 0 ? true : _ref$canStartPayment,
    dataHook = _ref.dataHook,
    currency = _ref.currency,
    _ref$withLoader = _ref.withLoader,
    withLoader = _ref$withLoader === void 0 ? false : _ref$withLoader,
    _ref$isMobile = _ref.isMobile,
    isMobile = _ref$isMobile === void 0 ? false : _ref$isMobile;
  const data = useExpressCheckoutButtonsData();
  const _useFunctionResultObs = useFunctionResultObservation({
      resultPropName: RESULT_PROP_NAME
    }),
    withObservation = _useFunctionResultObs.withObservation;
  const _ref2 = data ?? {},
    legacyCurrency = _ref2.legacyCurrency,
    locale = _ref2.locale,
    domain = _ref2.domain,
    meta = _ref2.meta,
    demoMode = _ref2.demoMode,
    onCancel = _ref2.onCancel,
    onClick = _ref2.onClick,
    setCurrency = _ref2.setCurrency,
    isExpressSupported = _ref2.isExpressSupported,
    isDisplayErrorModalsEnabled = _ref2.isDisplayErrorModalsEnabled;
  useEffect(() => {
    if (currency && setCurrency) {
      setCurrency(currency);
    }
  }, [currency, setCurrency]);
  const onShippingContactSelected = withObservation(data, 'onShippingContactSelected');
  const onPaymentAuthorized = withObservation(data, 'onPaymentAuthorized');
  const onShippingMethodSelected = withObservation(data, 'onShippingMethodSelected');
  const getInitialPaymentData = withObservation(data, 'getInitialPaymentData');
  const _useCheckoutErrorModa = useCheckoutErrorModal({
      isShippable
    }),
    modalType = _useCheckoutErrorModa.modalType,
    isCheckoutErrorModalOpen = _useCheckoutErrorModa.isCheckoutErrorModalOpen,
    openCheckoutErrorModal = _useCheckoutErrorModa.openCheckoutErrorModal,
    closeCheckoutErrorModal = _useCheckoutErrorModa.closeCheckoutErrorModal;
  const onClickHandler = useCallback(() => {
    onClick();
    if (modalType) {
      openCheckoutErrorModal();
      return {
        canceled: true
      };
    }
    return {
      canceled: !canStartPayment
    };
  }, [canStartPayment, onClick, modalType, openCheckoutErrorModal]);
  const _useState = useState(undefined),
    numberOfButtons = _useState[0],
    setNumberOfButtons = _useState[1];
  const shouldRender = data && Number(estimatedTotal) !== 0 && isExpressSupported;
  const isWidgetLoading = numberOfButtons === undefined;
  const isDataLoading = estimatedTotal === undefined;
  const isLoading = (isWidgetLoading || isDataLoading) && shouldRender;
  const state = getState({
    isLoading,
    shouldRender,
    numberOfButtons
  });
  useEffect(() => {
    if (!data) {
      return;
    }
    if (state === ExpressCheckoutButtonsState.LOADING) {
      data.onStateChange({
        state,
        numberOfButtons: undefined
      });
    } else {
      data.onStateChange({
        numberOfButtons: numberOfButtons ?? 0,
        state
      });
    }
  }, /* eslint-disable react-hooks/exhaustive-deps*/[Boolean(data), state]);
  if (!shouldRender) {
    return null;
  }
  const _getEstimatedPaymentD = getEstimatedPaymentData(estimatedTotal),
    orderItems = _getEstimatedPaymentD.orderItems,
    paymentBreakdown = _getEstimatedPaymentD.paymentBreakdown,
    paymentAmount = _getEstimatedPaymentD.paymentAmount;
  const shouldShowCashierWidgetWrapper = layout === 'horizontal' && numberOfButtons && numberOfButtons > 0;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    "data-hook": dataHook ?? ExpressCheckoutButtonsDataHooks.root,
    "data-wix-hideable": true,
    className: isLoading ? classes.hide : undefined
  }, /*#__PURE__*/React.createElement("div", {
    className: shouldShowCashierWidgetWrapper ? classes.cashierWidgetWrapper : undefined
  }, /*#__PURE__*/React.createElement(CashierExpressCheckoutWidgetLazy, {
    currency: currency ?? legacyCurrency,
    demoMode: demoMode,
    theme: theme,
    buttonStyle: buttonStyle,
    layout: layout,
    locale: locale,
    domain: domain,
    meta: meta,
    paymentLabel: "forApplePay",
    orderItems: orderItems,
    requestShipping: isShippable,
    paymentBreakdown: paymentBreakdown,
    paymentAmount: paymentAmount,
    onPaymentMethodsAmountInit: buttonsCount => {
      setNumberOfButtons(buttonsCount);
    },
    onPaymentAuthorized: paymentInfo => onPaymentAuthorized(paymentInfo, isShippable),
    onCancel: () => onCancel(),
    onClick: onClickHandler,
    onShippingMethodSelected: shippingMethod => onShippingMethodSelected(shippingMethod),
    onShippingContactSelected: shippingContact => onShippingContactSelected(shippingContact),
    getInitialPaymentData: () => getInitialPaymentData()
  }))), withLoader && isLoading ? /*#__PURE__*/React.createElement("div", {
    "data-hook": ExpressCheckoutButtonsDataHooks.loader,
    className: classes.skeletonWrapper
  }, /*#__PURE__*/React.createElement(Skeleton, {
    layout: layout,
    isMobile: isMobile
  })) : null, isDisplayErrorModalsEnabled && modalType && /*#__PURE__*/React.createElement(ExpressCheckoutErrorModal, {
    isOpen: isCheckoutErrorModalOpen,
    modalType: modalType,
    onClose: closeCheckoutErrorModal
  }));
};
//# sourceMappingURL=ExpressCheckoutButtons.js.map